import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MessageModal from "./MessageWindow";
import {
  UtensilsCrossed,
  Eye,
  EyeOff,
  AlertCircle,
  Loader,
  ChefHat,
  Mail,
  Lock,
  User,
} from "lucide-react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { formatString } from "../utils/stringUtils";
import { getApiUrl } from "../config/api";
import { signInWithGoogle } from "../utils/oauthAuth";

const Register = () => {
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    message: "",
    subtitle: "",
  });
  const [isOAuthSignIn, setIsOAuthSignIn] = useState(false);
  const [stats, setStats] = useState({
    restaurants: 0,
    users: 0,
    reviews: 0,
  });
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [fullNameInput, setFullNameInput] = useState("");

  const navigate = useNavigate();

  // Fetch statistics on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const apiUrl = getApiUrl("/stats");
        
        const response = await fetch(apiUrl);
        
        // Check if response is actually JSON (not HTML)
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          if (process.env.NODE_ENV === 'development') {
            await response.text(); // Consume the response body
            console.error("Stats endpoint returned non-JSON response:", {
              status: response.status,
              contentType: contentType,
              url: apiUrl,
            });
          }
          setStatsLoaded(true);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
          setStatsLoaded(true);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.error("Stats endpoint returned error:", response.status, response.statusText);
          }
          setStatsLoaded(true);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Failed to fetch statistics:", error);
        }
        setStatsLoaded(true); // Set to true even on error to avoid infinite loading
      }
    };
    fetchStats();
  }, []);

  // Check username availability
  const checkUsername = async () => {
    if (!formData.username || formData.username.length < 3) {
      setUsernameAvailable(false);
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await fetch(
        getApiUrl(`/users/${formData.username}`)
      );
      setUsernameAvailable(response.status === 404);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error checking username:", error);
      }
      setUsernameAvailable(false);
    } finally {
      setCheckingUsername(false);
    }
  };

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numeric: /[0-9]/.test(password),
    };

    if (!requirements.length)
      return "Password must be at least 8 characters long";
    if (!requirements.uppercase)
      return "Password must contain an uppercase letter";
    if (!requirements.lowercase)
      return "Password must contain a lowercase letter";
    if (!requirements.numeric) return "Password must contain a number";

    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleAchievement = (achievementData) => {
    const points = achievementData["points"];
    const id = formatString(achievementData["id"]);
    setModalData({
      title: `New Achievement`,
      subtitle: `${id}`,
      message: `Congrats! You earned ${points} points!`,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!usernameAvailable) {
        throw new Error("Username is not available");
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error("Passwords do not match");
      }

      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        throw new Error(passwordError);
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const userData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        uid: userCredential.user.uid,
        points: {
          generalPoints: 0,
          postPoints: 0,
          reviewPoints: 0,
        },
        playlists: [],
        emailVerified: false,
        achievements: [],
        numOfLists: 0,
      };

      const response = await fetch(getApiUrl("/users"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        await userCredential.user.delete();
        throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        await userCredential.user.delete();
        throw new Error(data.detail || `Failed to create user profile: ${response.status}`);
      }

      await auth.signOut();

      if (response.ok && data.newAchievements) {
        handleAchievement(data.newAchievements);
      } else {
        setModalData({
          title: "Account Created!",
          message: "Your account has been successfully created. Please log in.",
        });
        setModalVisible(true);
      }
    } catch (err) {
      let errorMessage = err.message;
      if (err.code === "auth/email-already-in-use") {
        errorMessage = "This email is already registered. Please log in instead.";
      } else if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError")) {
        errorMessage = "Cannot connect to backend API. Make sure your backend server is running.";
      }
      setError(errorMessage);
      if (auth.currentUser) {
        try {
          await auth.currentUser.delete();
        } catch (deleteError) {
          // Silently handle delete error
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await signInWithGoogle();
      if (result.success) {
        setIsOAuthSignIn(true);
        if (result.isNewUser && result.userData.newAchievements) {
          handleAchievement(result.userData.newAchievements);
        } else {
          navigate("/map");
        }
      }
    } catch (err) {
      let errorMessage = err.message;
      if (err.code === "auth/popup-closed-by-user") {
        errorMessage = "Sign-in popup was closed. Please try again.";
      } else if (err.code === "auth/popup-blocked") {
        errorMessage = "Popup was blocked. Please allow popups for this site.";
      } else if (err.code === "auth/account-exists-with-different-credential") {
        errorMessage = "An account already exists with this email. Please sign in with your original method.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image & Branding */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(/food-hero.jpg)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/80 via-orange-600/70 to-orange-700/80" />
        
        {/* Floating Elements */}
        <motion.div
          animate={{ y: [-10, 10, -10] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 right-20"
        >
          <ChefHat className="w-16 h-16 text-white/30" />
        </motion.div>
        <motion.div
          animate={{ y: [10, -10, 10] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-32 left-16"
        >
          <UtensilsCrossed className="w-20 h-20 text-white/20" />
        </motion.div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <h1 className="text-6xl font-bold mb-4">Foodify</h1>
            <p className="text-xl opacity-90 mb-8 max-w-md leading-relaxed">
              Discover, share, and connect with food lovers around the world.
              Your culinary journey starts here.
            </p>
            
            {/* Stats */}
            {statsLoaded && (
              <div className="flex gap-8 mt-12">
                {[
                  { number: stats.restaurants, label: "Restaurants" },
                  { number: stats.users, label: "Food Lovers" },
                  { number: stats.reviews, label: "Reviews" },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold">
                      {Math.floor(stat.number / 10) * 10}+
                    </div>
                    <div className="text-sm opacity-80">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#fef8f3] overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md my-8"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-4xl font-bold text-orange-500">Foodify</h1>
            <p className="text-gray-500 mt-2">Your culinary journey awaits</p>
          </div>

          {/* Form */}
          <AnimatePresence mode="wait">
            <motion.form
              key="signup"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="mb-6">
                <h2 className="text-3xl font-semibold text-gray-900">
                  Join Foodify
                </h2>
                <p className="text-gray-500 mt-2">
                  Create an account to start your food journey
                </p>
              </div>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, x: -20, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 20, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg"
                  >
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Name Field */}
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-900 mb-2">
                    Full Name
                  </label>
                  <div className="relative mt-2">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="John Doe"
                      value={fullNameInput || (formData.firstName && formData.lastName 
                        ? `${formData.firstName} ${formData.lastName}`
                        : formData.firstName || formData.lastName
                        ? `${formData.firstName} ${formData.lastName}`.trim()
                        : "")}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFullNameInput(value);
                        
                        // Split the name into first and last name
                        const trimmed = value.trim();
                        if (trimmed) {
                          const parts = trimmed.split(/\s+/);
                          setFormData((prev) => ({
                            ...prev,
                            firstName: parts[0] || "",
                            lastName: parts.slice(1).join(" ") || "",
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            firstName: "",
                            lastName: "",
                          }));
                        }
                      }}
                      onBlur={() => {
                        // Clear the local input state on blur, let it sync with formData
                        if (formData.firstName || formData.lastName) {
                          setFullNameInput("");
                        }
                      }}
                      required
                      className="w-full pl-12 pr-4 h-12 rounded-lg bg-gray-100 border-none focus:ring-2 focus:ring-orange-500/30 transition-all"
                    />
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Username Field */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-900 mb-2">
                  Username
                </label>
                <div className="flex items-center space-x-2 mt-2">
                  <div className="relative flex-1">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="Choose a username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      className={`w-full pl-12 pr-4 h-12 rounded-lg border-none focus:ring-2 transition-all ${
                        usernameAvailable === null
                          ? "bg-gray-100 focus:ring-orange-500/30"
                          : usernameAvailable
                          ? "bg-green-50 focus:ring-green-500/30"
                          : "bg-red-50 focus:ring-red-500/30"
                      }`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={checkUsername}
                    disabled={checkingUsername || !formData.username || formData.username.length < 3}
                    className="px-5 h-12 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-[0_8px_30px_-6px_rgba(249,115,22,0.3)]"
                  >
                    {checkingUsername ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      "Check"
                    )}
                  </button>
                </div>
                {checkingUsername && (
                  <p className="mt-2 text-sm text-gray-500 flex items-center">
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    Checking availability...
                  </p>
                )}
                {!checkingUsername && usernameAvailable !== null && (
                  <p className={`mt-2 text-sm font-medium ${
                    usernameAvailable ? "text-green-600" : "text-red-600"
                  }`}>
                    {usernameAvailable ? "✓ Username is available" : "✗ Username is taken"}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                  Email Address
                </label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="hello@foodify.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 h-12 rounded-lg bg-gray-100 border-none focus:ring-2 focus:ring-orange-500/30 transition-all"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2">
                  Password
                </label>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-12 h-12 rounded-lg bg-gray-100 border-none focus:ring-2 focus:ring-orange-500/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900 mb-2">
                  Confirm Password
                </label>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-12 h-12 rounded-lg bg-gray-100 border-none focus:ring-2 focus:ring-orange-500/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || checkingUsername || usernameAvailable === false}
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 via-orange-500 to-yellow-500 hover:opacity-90 transition-opacity rounded-lg shadow-[0_8px_30px_-6px_rgba(249,115,22,0.3)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Creating Account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#fef8f3] text-gray-500">
                    or continue with
                  </span>
                </div>
              </div>

              {/* Social Login */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full h-12 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-orange-500/30 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Google
                </button>
              </div>

              {/* Terms */}
              <p className="text-center text-sm text-gray-500 mt-6">
                By signing up, you agree to our{" "}
                <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">
                  Privacy Policy
                </a>
              </p>
            </motion.form>
          </AnimatePresence>

          <div className="mt-6 text-center">
            <span className="text-sm text-gray-500">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-orange-500 hover:underline font-medium"
              >
                Log in
              </button>
            </span>
          </div>
        </motion.div>
      </div>
      <MessageModal
        show={modalVisible}
        title={modalData.title}
        subtitle={modalData.subtitle}
        message={modalData.message}
        onClose={() => {
          setModalVisible(false);
          if (isOAuthSignIn) {
            navigate("/map");
            setIsOAuthSignIn(false);
          } else {
            navigate("/login", {
              state: {
                message: "Account created successfully! Please log in.",
              },
            });
          }
        }}
      />
    </div>
  );
};

export default Register;
